import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import { CheckoutState, SplashPage } from "contexts/Subscription";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";
import { SearchParams } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import StripeCheckout from "components/StripeCheckout";
import { trackEvent } from "utils/tracking";

import Subscription from "../Subscription";
import UpgradeFeatureDescription from "../UpgradeFeatureDescription";

const getUpgradeSourceFromParam = (
  param: string | null,
): UpgradeDialogSource | null => {
  if (!param) return null;

  const enumKey = param as keyof typeof UpgradeDialogSource;
  if (UpgradeDialogSource[enumKey]) {
    return UpgradeDialogSource[enumKey];
  }

  const values = Object.values(UpgradeDialogSource);
  if (values.includes(param as UpgradeDialogSource)) {
    return param as UpgradeDialogSource;
  }

  return null;
};

const getPlanFromParam = (
  param: string | null,
): Plans | AddOnItemName | null => {
  if (!param) return null;

  const planValues = Object.values(Plans);
  if (planValues.includes(param as Plans)) {
    return param as Plans;
  }

  const addOnValues = Object.values(AddOnItemName);
  if (addOnValues.includes(param as AddOnItemName)) {
    return param as AddOnItemName;
  }

  return null;
};

const getSplashPageFromParam = (param: string | null): SplashPage | null => {
  if (!param) return null;

  const values = Object.values(SplashPage);
  if (values.includes(param as SplashPage)) {
    return param as SplashPage;
  }

  return null;
};

export default function SubscribePage() {
  const [searchParams] = useSearchParams();

  // Get upgrade source from query param
  const sourceParam = searchParams.get(SearchParams.UPGRADE_DIALOG_SOURCE);
  const upgradeSource = useMemo(
    () => getUpgradeSourceFromParam(sourceParam),
    [sourceParam],
  );

  // Build checkout state from query params
  const checkoutState = useMemo((): CheckoutState | null => {
    const planParam = searchParams.get(SearchParams.PLAN_NAME);
    const plan = getPlanFromParam(planParam);

    if (!plan) return null;

    return {
      plan,
      pageSource: searchParams.get(SearchParams.PAGE_SOURCE),
      source: searchParams.get(SearchParams.CHECKOUT_SOURCE),
      coupon: searchParams.get(SearchParams.COUPON),
      splashPageSource: getSplashPageFromParam(
        searchParams.get(SearchParams.SPLASH_PAGE_SOURCE),
      ),
    };
  }, [searchParams]);

  useEffect(() => {
    trackEvent("Shown Paywall Dialog", {
      Reason: upgradeSource,
      "From Mobile App": true,
    });
  }, [upgradeSource]);

  const renderPlanSelection = () => (
    <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 16, mb: -1 }} color="textSecondary">
          Unlock the tools to
        </Typography>
        <Typography variant="h3">grow faster ⚡</Typography>
      </Box>

      {upgradeSource && (
        <UpgradeFeatureDescription upgradeDialogSource={upgradeSource} />
      )}

      <Subscription upgradeDialogSource={upgradeSource} />
    </DialogContent>
  );

  return (
    <Dialog
      open={true}
      fullScreen
      disableEscapeKeyDown
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "background.default",
        },
      }}
    >
      {checkoutState?.plan ? (
        <StripeCheckout
          openCheckoutDialog={checkoutState || undefined}
          noBackButton
          upgradeDialogSourceOverride={upgradeSource}
        />
      ) : (
        renderPlanSelection()
      )}
    </Dialog>
  );
}
